import React, { useMemo } from "react";
import { House } from "lucide-react";
import StaticMapboxComponent from "@/components/Common/StaticMapboxComponent";
import usePolygons from "@/hooks/usePolygons";
import { hasUsableCoordinates, normalizeZipCodes, parseDrawnPolygonWKT } from "@/helpers/mapbox";
import "./WantListingThumb.scss";

export const thumbSource = (map) => {
  if (!map) return null;
  const located = hasUsableCoordinates(map.longitude, map.latitude);
  if (!map.imageUrl && !located) return null;
  return { imageUrl: map.imageUrl || null, located };
};

const pointsOf = (value) => {
  if (!Array.isArray(value)) return [];
  if (value.length === 2 && value.every((n) => typeof n === "number")) return [value];
  if (value.length && typeof value[0] === "object" && !Array.isArray(value[0]) && "lng" in value[0]) {
    return value.map((point) => [point.lng, point.lat]);
  }
  return value.flatMap(pointsOf);
};

export const usableShape = (coordinates) => {
  const points = pointsOf(coordinates).filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));
  if (points.length < 4) return false;
  const lngs = points.map(([lng]) => lng);
  const lats = points.map(([, lat]) => lat);
  return Math.max(...lngs) - Math.min(...lngs) > 0.0005 && Math.max(...lats) - Math.min(...lats) > 0.0005;
};

const LiveThumb = ({ map, width, height }) => {
  const drawn = useMemo(() => parseDrawnPolygonWKT(map.drawnPolygon), [map.drawnPolygon]);
  const zipCodes = useMemo(() => normalizeZipCodes(map.zipCodes), [map.zipCodes]);
  const { polygonCoordinates } = usePolygons(zipCodes, Boolean(map.imageUrl || drawn));
  const hasZipShapes = zipCodes.length > 0 && usableShape(polygonCoordinates);
  return (
    <StaticMapboxComponent
      padding={8}
      width={width}
      height={height}
      center={[Number(map.longitude), Number(map.latitude)]}
      radiusMiles={drawn ? null : map.radius}
      polygonCoordinates={drawn || (hasZipShapes ? polygonCoordinates : undefined)}
      showPolygon={Boolean(drawn) || hasZipShapes}
      storedMapUrl={map.imageUrl || undefined}
    />
  );
};

const WantListingThumb = ({ map, width = 64, height = 40, title = "Buyer Want-Listing", className = "" }) => {
  const source = thumbSource(map);
  const style = { width: `${width}px`, height: `${height}px` };
  if (!source) {
    return (
      <span className={`want-listing-thumb is-empty ${className}`} style={style} aria-hidden="true">
        <House size={Math.min(18, height - 16)} strokeWidth={1.8} />
      </span>
    );
  }
  return (
    <span className={`want-listing-thumb ${className}`} style={style} role="img" aria-label={`Map of ${title}`}>
      {source.located ? (
        <LiveThumb map={map} width={width} height={height} />
      ) : (
        <img src={source.imageUrl} alt="" loading="lazy" decoding="async" />
      )}
    </span>
  );
};

export default WantListingThumb;
